import React from "react"
import Icon from "../../../components/AppIcon"
import Input from "../../../components/ui/Input"
import Select from "../../../components/ui/Select"
import Button from "../../../components/ui/Button"
import { generateLocalId } from "../../../utils/courseHelpers"

const QUESTION_TYPE_OPTIONS = [
  { value: "multiple_choice", label: "Multiple Choice" },
  { value: "true_false", label: "True / False" },
]

const createEmptyQuestion = () => ({
  id: generateLocalId(),
  type: "multiple_choice",
  prompt: "",
  options: [
    { id: generateLocalId(), text: "" },
    { id: generateLocalId(), text: "" },
  ],
  correctOptionId: "",
  resourceHint: "",
})

const trueFalseOptions = () => [
  { id: "true", text: "True" },
  { id: "false", text: "False" },
]

const QuestionListEditor = ({ questions, onChange }) => {
  const addQuestion = () => {
    onChange([...(questions || []), createEmptyQuestion()])
  }

  const updateQuestion = (index, updates) => {
    const next = [...questions]
    next[index] = { ...next[index], ...updates }
    onChange(next)
  }

  const deleteQuestion = (index) => {
    onChange(questions?.filter((_, i) => i !== index))
  }

  const handleTypeChange = (index, type) => {
    if (type === "true_false") {
      updateQuestion(index, { type, options: trueFalseOptions(), correctOptionId: "" })
    } else {
      updateQuestion(index, {
        type,
        options: [
          { id: generateLocalId(), text: "" },
          { id: generateLocalId(), text: "" },
        ],
        correctOptionId: "",
      })
    }
  }

  const updateOption = (qIndex, oIndex, text) => {
    const question = questions?.[qIndex]
    const options = [...question?.options]
    options[oIndex] = { ...options[oIndex], text }
    updateQuestion(qIndex, { options })
  }

  const addOption = (qIndex) => {
    const question = questions?.[qIndex]
    updateQuestion(qIndex, { options: [...question?.options, { id: generateLocalId(), text: "" }] })
  }

  const removeOption = (qIndex, oIndex) => {
    const question = questions?.[qIndex]
    const removedId = question?.options?.[oIndex]?.id
    updateQuestion(qIndex, {
      options: question?.options?.filter((_, i) => i !== oIndex),
      correctOptionId: question?.correctOptionId === removedId ? "" : question?.correctOptionId,
    })
  }

  return (
    <div className="space-y-4">
      {questions?.length === 0 && (
        <p className="text-sm text-muted-foreground">No questions yet. Add your first question below.</p>
      )}

      {questions?.map((question, qIndex) => (
        <div key={question?.id} className="border border-border rounded-lg p-4 space-y-3 bg-background">
          <div className="flex items-start gap-3">
            <span className="text-xs font-semibold text-muted-foreground pt-2">Q{qIndex + 1}</span>
            <div className="flex-1 space-y-3">
              <Input
                placeholder="Question prompt"
                value={question?.prompt}
                onChange={(e) => updateQuestion(qIndex, { prompt: e?.target?.value })}
              />
              <div className="w-48">
                <Select
                  options={QUESTION_TYPE_OPTIONS}
                  value={question?.type}
                  onChange={(value) => handleTypeChange(qIndex, value)}
                />
              </div>

              <div className="space-y-2">
                {question?.options?.map((option, oIndex) => (
                  <div key={option?.id} className="flex items-center gap-2">
                    <input
                      type="radio"
                      checked={question?.correctOptionId === option?.id}
                      onChange={() => updateQuestion(qIndex, { correctOptionId: option?.id })}
                      className="flex-shrink-0"
                    />
                    {question?.type === "true_false" ? (
                      <span className="text-sm text-foreground flex-1">{option?.text}</span>
                    ) : (
                      <Input
                        placeholder={`Option ${oIndex + 1}`}
                        value={option?.text}
                        onChange={(e) => updateOption(qIndex, oIndex, e?.target?.value)}
                        className="flex-1"
                      />
                    )}
                    {question?.type === "multiple_choice" && question?.options?.length > 2 && (
                      <Button variant="ghost" size="icon" iconName="X" iconSize={14} onClick={() => removeOption(qIndex, oIndex)} />
                    )}
                  </div>
                ))}
                {question?.type === "multiple_choice" && (
                  <Button variant="ghost" size="sm" iconName="Plus" iconPosition="left" onClick={() => addOption(qIndex)}>
                    Add Option
                  </Button>
                )}
              </div>
              {!question?.correctOptionId && (
                <p className="text-xs text-warning">Select the correct answer above.</p>
              )}
              <Input
                label="Resource hint (shown to learners who answer this wrong)"
                placeholder="e.g. Review Module 2, Lesson 3"
                value={question?.resourceHint || ""}
                onChange={(e) => updateQuestion(qIndex, { resourceHint: e?.target?.value })}
              />
            </div>
            <Button variant="ghost" size="icon" iconName="Trash2" iconSize={16} onClick={() => deleteQuestion(qIndex)} />
          </div>
        </div>
      ))}

      <Button variant="outline" iconName="Plus" iconPosition="left" onClick={addQuestion}>
        Add Question
      </Button>
    </div>
  )
}

export default QuestionListEditor
